import { ColDef, GridApi, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { isDefined, isEntity } from '@softline/core';
import type { ColGroupDef } from 'ag-grid-community/dist/types/src/entities/colDef';

export interface QueryColumn {
  id: string;
  field: string;
}

export interface QuerySort {
  colId: string;
  sort: 'asc' | 'desc';
}

export interface QueryPivot {
  id: string;
  field: string;
}

export interface QueryValue {
  id: string;
  aggFunc: 'sum' | 'avg' | 'min' | 'max';
  field: string;
}


export interface IQueryFilter {
  filterType: string;
}

export interface QuerySetFilter extends IQueryFilter {
  filterType: 'set';
  values: any[];
}

export interface QueryDateFilter extends IQueryFilter {
  filterType: 'date';
  type: 'equals' | 'notEqual' | 'lessThan' | 'lessThanOrEqual' | 'greaterThan' | 'greaterThanOrEqual' | 'inRange';
  dateFrom: string;
  dateTo: string | null;
}

export interface QueryNumberFilter extends IQueryFilter {
  filterType: 'number';
  type: 'equals' | 'notEqual' | 'lessThan' | 'lessThanOrEqual' | 'greaterThan' | 'greaterThanOrEqual' | 'inRange';
  filter: number;
  filterTo: number | null;
}

export interface QueryTextFilter extends IQueryFilter {
  filterType: 'text';
  type: 'equals' | 'notEqual' | 'contains' | 'notContains' | 'startsWith' | 'endsWith';
  filter: string;
}

export interface QueryCombinedFilter extends IQueryFilter {
  operator: 'AND' | 'OR';
  conditions: QueryFilter;
}

export type QueryFilter = QuerySetFilter | QueryDateFilter | QueryNumberFilter | QueryTextFilter | QueryCombinedFilter;

export interface QueryFilterModel {
  [colId: string]: QueryFilter
}

export interface Query {
  columns: QueryColumn[];
  rowGroupCols: QueryColumn[];
  sortModel: QuerySort[];
  valueCols: QueryValue[];
  filterModel: QueryFilterModel;
  groupMode: boolean;
  startRow: number;
  endRow: number;
  groupKeys: string[];
  pivotMode: boolean;
  pivotCols: QueryColumn[];
}

export class QueryBuilder {

  private columns: QueryColumn[] = [];
  private rowGroupCols: QueryColumn[] = [];
  private sortModel: QuerySort[] = [];
  private valueCols: QueryValue[] = [];
  private filterModel: any = {};
  private pivotMode = false;
  private groupMode = false;
  private pivotCols: QueryColumn[] = [];
  private groupKeys: string[] = [];

  constructor(private startRow = 0, private endRow = 100) {
  }

  start(number: number): this {
    this.startRow = number;
    return this;
  }

  end(number: number): this {
    this.endRow = number;
    return this;
  }

  static fromQuery(query: Query): QueryBuilder {
    const builder = new QueryBuilder(query.startRow, query.endRow);
    query.columns.forEach(c => builder.addColumnIfNotExists(c.id, c.field));
    query.rowGroupCols.forEach(c => builder.group(c.id));
    query.valueCols.forEach(c => builder.value(c.id, c.aggFunc));
    query.sortModel.forEach(s => builder.sort(s.colId, s.sort));
    Object.keys(query.filterModel).forEach(colId => {
      const filter = query.filterModel[colId];
      builder.filter(colId, filter);
    });
    query.pivotCols.forEach(c => builder.pivot(c.id));
    builder.pivotMode = query.pivotMode;
    builder.groupMode = query.groupMode;
    builder.groupKeys = [...query.groupKeys];
    return builder;
  }

  fromGrid(api: GridApi, request: IServerSideGetRowsRequest, sortKeyMap?: Map<string, string>): this {
    if(request.groupKeys.length !== request.rowGroupCols.length)
      return this.fromSSRMRequest(request, sortKeyMap);
    const cols = api.getColumnDefs();
    this.startRow = request.startRow ?? 0;
    this.endRow = request.endRow ?? 100;
    if(!cols)
      return this;

    for (const col of cols) {
      const field = (col as ColDef)?.field;
      if(field)
        this.addColumnIfNotExists((col as ColDef)?.colId ?? field, (col as ColDef).field)
    }
    let index = 0;
    for (const col of request.rowGroupCols) {
      const filter = request.groupKeys[index] as any;
      if(filter) {
        this.addColumnIfNotExists(col.id, col.field);
        switch (typeof filter) {
          case 'string':
            this.filter(col.id, { type: 'equals', filterType: 'text', filter });
            break;
          case 'number':
            this.filter(col.id, { type: 'equals', filterType: 'number', filter, filterTo: null });
            break
          case 'object':
            if((typeof filter.id) === 'number') {
              this.filter(col.id+'.id', { type: 'equals', filterType: 'number', filter: filter.id, filterTo: null });
            }
            break
        }
      }
      index++;
    }
    if(request.filterModel) {
      Object.keys(request.filterModel).forEach(colId => {
        const filter = request.filterModel![colId];
        if(filter) {
          this.addColumnIfNotExists(colId);
          this.filter(colId, filter)
        }
      });
    }

    if(request.sortModel) {
      request.sortModel.forEach(s => {
        if(isDefined(sortKeyMap) && sortKeyMap?.has(s.colId)) {
          this.sort(sortKeyMap.get(s.colId) as string, s.sort as 'asc' | 'desc');
        }
        else {
          this.sort(s.colId, s.sort as 'asc' | 'desc');
        }
      });
    }
    return this;
  }

  fromGridApi(api: GridApi): this {
    const cols = api.getColumnDefs();
    this.startRow = 0;
    this.endRow = 10000;
    if(!cols)
      return this;
    while (cols.length > 0) {
      const col = cols.pop();
      const field = (col as ColDef)?.field;
      if(field)
        this.addColumnIfNotExists((col as ColDef)?.colId ?? field, (col as ColDef).field)
      if((col as ColGroupDef<any>).children?.length > 0)
        cols.push(...(col as ColGroupDef<any>).children)
    }
    return this;
  }

  fromSSRMRequest(query: IServerSideGetRowsRequest, sortKeyMap?: Map<string, string>): this {
    this.startRow = query.startRow ?? 0;
    this.endRow = query.endRow ?? 100;
    if(query.rowGroupCols) {
      query.rowGroupCols.forEach((c, index) => {
        if(index <= query.groupKeys?.length) {
          this.addColumnIfNotExists(c.id, c.field);
          const value = query.groupKeys && query.groupKeys.length > index ? query.groupKeys[index] : undefined;
          this.group(c.id, value);
        }
      });
    }
    if(query.valueCols) {
      query.valueCols.forEach(v => {
        this.addColumnIfNotExists(v.id, v.field);
        if(v.aggFunc)
          this.value(v.id, v.aggFunc as 'sum' | 'avg' | 'min' | 'max');
      });
    }
    if(query.pivotCols) {
      query.pivotCols.forEach(c => {
        this.addColumnIfNotExists(c.id, c.field);
        this.pivot(c.id);
      });
    }
    if(query.filterModel) {
      Object.keys(query.filterModel).forEach(colId => {
        const filter = query.filterModel![colId];
        if(filter) {
          this.addColumnIfNotExists(colId);
          this.filter(colId, filter)
        }
      });
    }
    if(query.sortModel) {
      for(const s of query.sortModel) {
        if(this.columns.findIndex(c => c.id === s.colId) === -1)
          continue;
        if(this.groupMode && this.rowGroupCols.findIndex(c => c.id === s.colId) === -1 && this.valueCols.findIndex(c => c.id === s.colId) === -1)
          continue;
        if(isDefined(sortKeyMap) && sortKeyMap?.has(s.colId)) {
          this.sort(sortKeyMap.get(s.colId) as string, s.sort as 'asc' | 'desc');
        }
        else {
          this.sort(s.colId, s.sort as 'asc' | 'desc');
        }
      }
    }
    this.pivotMode = query.pivotMode ?? false;
    return this;
  }

  column(colId: string, field?: string): this {
    this.columns.push({id: colId, field: field ?? colId} as any);
    return this;
  }

  group(colId: string, value?: any): this {
    this.groupMode = true;
    if(value !== undefined && this.groupKeys.length !== this.rowGroupCols.length)
      throw new Error('Group keys must be added in the same order as group columns');
    const column = this.addColumnIfNotExists(colId);
    this.rowGroupCols.push({ ...column, displayName: '' } as any);
    if(value !== undefined)
      this.groupKeys.push(value);
    return this;
  }

  value(colId: string, aggFunc: string): this {
    this.groupMode = true;
    const column = this.addColumnIfNotExists(colId);
    this.valueCols.push({...column, aggFunc, displayName: ''} as any);
    return this;
  }

  sort(colId: string, sort: 'asc' | 'desc'): this {
    this.addColumnIfNotExists(colId);
    this.sortModel.push({colId: colId, sort});
    return this;
  }

  pivot(colId: string): this {
    this.pivotMode = true;
    const column = this.addColumnIfNotExists(colId);
    this.pivotCols.push(column);
    return this;
  }

  filter(colId: string, filter: QueryFilter): this {
    this.addColumnIfNotExists(colId);
    this.filterModel[colId] = filter;
    return this;
  }

  filterSet(colId: string, values: any[]): this {
    this.addColumnIfNotExists(colId);
    this.filterModel[colId] = {
      filterType: 'set',
      values
    };
    return this;
  }

  filterDate(colId: string, type: 'equals' | 'notEqual' | 'lessThan' | 'lessThanOrEqual' | 'greaterThan' | 'greaterThanOrEqual', date: string): this {
    this.addColumnIfNotExists(colId);
    this.filterModel[colId] = {
      filterType: 'date',
      type,
      dateFrom: date.substring(0,10),
      dateTo: null
    }
    return this;
  }

  filterDateRange(colId: string, dateFrom: string, dateTo: string): this {
    this.addColumnIfNotExists(colId);
    this.filterModel[colId] = {
      filterType: 'date',
      type: 'inRange',
      dateFrom: dateFrom.substring(0,10),
      dateTo: dateTo.substring(0,10)
    }
    return this;
  }

  filterText(colId: string, type: 'equals' | 'notEqual' | 'contains' | 'notContains' | 'startsWith' | 'endsWith', filter: string): this {
    this.addColumnIfNotExists(colId);
    this.filterModel[colId] = {
      filterType: 'text',
      type,
      filter
    }
    return this;
  }

  filterNumber(colId: string, type: 'equals' | 'notEqual' | 'lessThan' | 'lessThanOrEqual' | 'greaterThan' | 'greaterThanOrEqual', filter: number): this {
    this.addColumnIfNotExists(colId);
    this.filterModel[colId] = {
      filterType: 'number',
      type,
      filter,
      filterTo: null
    }
    return this;
  }

  filterNumberRange(colId: string, filter: number, filterTo: number): this {
    this.addColumnIfNotExists(colId);
    this.filterModel[colId] = {
      filterType: 'number',
      type: 'inRange',
      filter,
      filterTo
    }
    return this;
  }

  build(): Query {
    return {
      columns: this.columns,
      rowGroupCols: this.rowGroupCols,
      sortModel: this.sortModel,
      valueCols: this.valueCols,
      filterModel: this.filterModel,
      startRow: this.startRow,
      groupMode: this.groupMode,
      endRow: this.endRow,
      groupKeys: this.groupKeys,
      pivotMode: this.pivotMode,
      pivotCols: this.pivotCols
    }
  }

  private addColumnIfNotExists(colId: string, field?: string): QueryColumn {
    let column = this.columns.find(c => c.id === colId);
    if(!column) {
      column = {id: colId, field: field ?? colId};
      this.columns.push(column);
    }
    return column;
  }

  copy(): QueryBuilder {
    const copy = new QueryBuilder(this.startRow, this.endRow);
    copy.columns = [...this.columns];
    copy.rowGroupCols = [...this.rowGroupCols];
    copy.sortModel = [...this.sortModel];
    copy.valueCols = [...this.valueCols];
    copy.filterModel = {...this.filterModel};
    copy.pivotMode = this.pivotMode;
    copy.groupMode = this.groupMode;
    copy.pivotCols = [...this.pivotCols];
    copy.groupKeys = [...this.groupKeys];
    return copy;
  }

}
